import React, { useState, useEffect, useCallback } from 'react';
import { H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Row, Media } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { PlusCircle, Edit, ArrowRight, X, DollarSign } from 'react-feather';
import ExpertWitnessReportDataModal from './ExpertWitnessReportDataModal';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import FileCloseModal from './FileCloseModal';
import { toast } from 'react-toastify';
import { fetchTableData, handleStateTransition } from '../../../../Api/ApiMethods';

const ExpertWitnessReportInformations = ({ arbitrationId, arbitrationData, refreshArbitrationData }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedExpertWitnessReport, setSelectedExpertWitnessReport] = useState(null);

    // Masraf modal state'leri
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [currentExpertWitnessReport, setCurrentExpertWitnessReport] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [expenseModalMode, setExpenseModalMode] = useState('add');
    const [isExpenseReadOnly, setIsExpenseReadOnly] = useState(false);

    // Dosya kapatma modal state'leri
    const [isFileCloseModalOpen, setIsFileCloseModalOpen] = useState(false);

    const expertWitnessReportApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ExpertWitnessReportInformation';

    const logSetData = (newData) => {
        console.log("ExpertWitnessReportInformations.jsx: setData CALLED with new data length:", newData?.length, newData);
        setData(newData);
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    useEffect(() => {
        if (arbitrationId) {
            // Modify the API endpoint to include arbitrationId as a query parameter
            const modifiedApiEndpoint = `${expertWitnessReportApiUrl}?arbitrationId=${arbitrationId}`;

            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '', // No search functionality
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
    }, [page, pageSize, arbitrationId, expertWitnessReportApiUrl]);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedExpertWitnessReport(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedExpertWitnessReport(null);
        setModalOpen(true);
    };

    const openModifyModal = (expertWitnessReport) => {
        setModalMode('modify');
        setSelectedExpertWitnessReport(expertWitnessReport);
        setModalOpen(true);
    };

    const refreshExpertWitnessReportData = () => {
        console.log("ExpertWitnessReportInformations.jsx: refreshExpertWitnessReportData CALLED. Current state:", { page, pageSize });
        if (arbitrationId) {
            // Modify the API endpoint to include arbitrationId as a query parameter
            const modifiedApiEndpoint = `${expertWitnessReportApiUrl}?arbitrationId=${arbitrationId}`;

            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '', // No search functionality
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
    };

    // Masraf modal handler fonksiyonları
    const handleAddExpenseModal = (expertWitnessReport) => {
        setCurrentExpertWitnessReport(expertWitnessReport);
        setSelectedExpense(null);
        setExpenseModalMode('add');
        setIsExpenseReadOnly(false);
        setIsExpenseModalOpen(true);
    };

    const handleEditExpenseModal = (expertWitnessReport, expense) => {
        const currentState = arbitrationData?.arbitrationState?.title;
        const isIslahOdemesiBekleniyor = currentState === 'Islah Ödemesi Bekleniyor';

        setCurrentExpertWitnessReport(expertWitnessReport);
        setSelectedExpense(expense);
        setExpenseModalMode('modify');
        setIsExpenseReadOnly(!isIslahOdemesiBekleniyor); // Sadece "Islah Ödemesi Bekleniyor" durumunda düzenlenebilir
        setIsExpenseModalOpen(true);
    };

    const closeExpenseModal = () => {
        setIsExpenseModalOpen(false);
        setCurrentExpertWitnessReport(null);
        setSelectedExpense(null);
        setIsExpenseReadOnly(false);
    };

    const refreshExpenseData = () => {
        // Masraf eklendikten/güncellendiksinde tabloyu yenile
        refreshExpertWitnessReportData();
    };

    // State transition function
    const handleArbitrationStateTransition = async () => {
        try {
            await handleStateTransition({ arbitrationId });
            refreshArbitrationData(); // Ana bileşendeki arbitration verisini yenile
        } catch (error) {
            console.error('State transition error:', error);
            // Toast mesajı handleStateTransition fonksiyonunda gösterildiği için burada tekrar göstermiyoruz
        }
    };

    // Dummy function for file closure (no functionality)
    const handleFileClose = () => {
        setIsFileCloseModalOpen(true);
    };

    // Function to get button text based on current state
    const getStateTransitionButtonText = () => {
        const currentState = arbitrationData?.arbitrationState?.title;

        switch (currentState) {
            case 'Bilirkişi Raporu Bekleniyor':
                return 'Islah Ödemesine Gönder';
            case 'Islah Ödemesi Bekleniyor':
                return 'Karara Gönder';
            default:
                return '';
        }
    };

    // Function to check if state transition button should be shown
    const shouldShowStateTransitionButton = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        return ['Bilirkişi Raporu Bekleniyor', 'Islah Ödemesi Bekleniyor'].includes(currentState);
    };

    // Function to check if file close button should be shown
    const shouldShowFileCloseButton = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        // Tekrar Başvuru Bekleniyor ve Kapama Bekleniyor durumlarında gösterme
        return !['Tekrar Başvuru Bekleniyor', 'Kapama Bekleniyor'].includes(currentState);
    };

    const renderCellWithTooltip = (row, field, defaultValue = '') => {
        const value = row[field] || defaultValue;
        return (
            <div
                title={value}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                }}
            >
                {value}
            </div>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    const renderComment = (comments) => {
        // API'den gelen comments array'inden ilk comment'i göster
        if (!comments || !Array.isArray(comments) || comments.length === 0) return '-';
        const firstComment = comments[0]?.comment || '';
        if (!firstComment) return '-';

        return (
            <div
                title={firstComment}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '400px',
                }}
            >
                {firstComment}
            </div>
        );
    };

    const columns = [
        {
            name: 'Geliş Tarihi',
            selector: row => formatDate(row.arrivalDate),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Son Gün',
            selector: row => formatDate(row.lastDay),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Rapor Türü',
            selector: row => row.reportType || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'reportType'),
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'Kusur Tespit',
            selector: row => row.ewDefectDeterminationFee ? `${row.ewDefectDeterminationFee}%` : '',
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Talep Tespit',
            selector: row => formatCurrency(row.ewClaimDeterminationFee),
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Islah Ücreti',
            selector: row => formatCurrency(row.amendmentFee),
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Rapor İçin Eylem',
            selector: row => row.actionForReport || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'actionForReport'),
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'Yorum',
            selector: row => {
                // API'den gelen comments array'inden ilk comment'i göster
                if (!row.comments || !Array.isArray(row.comments) || row.comments.length === 0) return '-';
                return row.comments[0]?.comment || '-';
            },
            cell: row => renderComment(row.comments),
            grow: 3,
            minWidth: '100px'
        },
        {
            name: 'Masraf',
            cell: (row) => {
                const expense = row.expenses; // Bilirkişi raporu objesinde expenses alanı
                const currentState = arbitrationData?.arbitrationState?.title;
                const isIslahOdemesiBekleniyor = currentState === 'Islah Ödemesi Bekleniyor';

                if (!expense) {
                    // Masraf yoksa sadece "Islah Ödemesi Bekleniyor" durumunda "Ekle" butonu göster
                    if (isIslahOdemesiBekleniyor) {
                        return (
                            <div
                                className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                onClick={() => handleAddExpenseModal(row)}
                                title="Masraf Ekle"
                                style={{ minWidth: '80px' }}
                            >
                                Ekle
                            </div>
                        );
                    } else {
                        // Diğer durumlarda masraf yoksa boş göster
                        return <div style={{ minWidth: '80px', textAlign: 'center' }}>-</div>;
                    }
                } else {
                    // Masraf varsa her durumda görüntüle (düzenleme yetkisi duruma göre)
                    const isReadOnly = !isIslahOdemesiBekleniyor;
                    return (
                        <div
                            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                            onClick={() => handleEditExpenseModal(row, expense)}
                            title={isReadOnly ? `${expense.expenseType || 'Masraf'} - Görüntülemek için tıklayın` : `${expense.expenseType || 'Masraf'} - Düzenlemek için tıklayın`}
                            style={{ cursor: 'pointer', minWidth: '80px' }}
                        >
                            <small className="text-primary fw-bold">
                                {expense.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(expense.amount) : '-'}
                            </small>
                        </div>
                    );
                }
            },
            ignoreRowClick: true,
            button: true,
            grow: 1.2,
            minWidth: '120px'
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
            grow: 1.2,
            minWidth: '100px'
        },
    ];

    return (
        <>
            <Row className="mt-4">
                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                <Media body className='text-end mt-3'>
                                    <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <PlusCircle size={18} className="me-1" />
                                        {' Bilirkişi Raporu Ekle'}
                                    </div>
                                    {shouldShowStateTransitionButton() && (
                                        <div
                                            className="btn btn-outline-success ms-2"
                                            onClick={handleArbitrationStateTransition}
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <ArrowRight size={18} className="me-1" />
                                            {getStateTransitionButtonText()}
                                        </div>
                                    )}
                                    {shouldShowFileCloseButton() && (
                                        <div
                                            className="btn btn-outline-secondary ms-2"
                                            onClick={handleFileClose}
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <X size={18} className="me-1" />
                                            Dosya Kapamaya Gönder
                                        </div>
                                    )}
                                </Media>
                            </div>

                            <div style={{ height: "40px" }}></div>

                            <div className='table-responsive'>
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    clearSelectedRows={clearSelectedRows}
                                    paginationTotalRows={totalRows}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                    striped
                                    noDataComponent={<div className="p-4 text-center">Bilirkişi raporu bulunamadı.</div>}
                                    progressPending={!data}
                                    progressComponent={<p>Yükleniyor...</p>}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <ExpertWitnessReportDataModal
                apiEndpoint={expertWitnessReportApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedExpertWitnessReport={selectedExpertWitnessReport}
                refreshData={refreshExpertWitnessReportData}
                arbitrationId={arbitrationId}
            />

            {/* Masraf Ekleme/Düzenleme Modalı */}
            <ExpenseDataModal
                apiEndpoint={process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense'}
                isOpen={isExpenseModalOpen}
                toggle={closeExpenseModal}
                mode={expenseModalMode}
                selectedExpense={selectedExpense}
                refreshData={refreshExpenseData}
                customerId={null}
                autoAssignCustomer={false}
                accidentFile={[]}
                callformId={null}
                relatedArbitrationSchema={currentExpertWitnessReport ? [String(currentExpertWitnessReport.id)] : []}
                arbitrationSchemaType="Islah Ödemesi"
                readOnlyView={isExpenseReadOnly}
            />

            {/* Dosya Kapatma Modalı */}
            <FileCloseModal
                isOpen={isFileCloseModalOpen}
                toggle={() => setIsFileCloseModalOpen(false)}
                arbitrationId={arbitrationId}
                refreshArbitrationData={refreshArbitrationData}
            />
        </>
    );
};

export default ExpertWitnessReportInformations;
